import { useEffect, useId, useRef, useState } from 'react';

/** The slice of a metric dictionary entry the tooltip needs. The shared primitive takes plain props, so it knows nothing about any one dashboard. */
export interface MetricInfoContent {
  displayName: string;
  definition: string;
  formula: string;
  included: string;
  excluded: string;
  sourceModule: string;
}

/**
 * A small "i" button that explains one figure in plain language: what it is, how it is calculated, what it includes and leaves out, and where it
 * comes from. It opens on click or Enter/Space (not hover only, so keyboard and touch users get it too), closes on Escape or when focus leaves,
 * and is wired with aria-expanded / aria-controls. Renders nothing when there is no definition, so a figure without one is never given an empty box.
 */
export function MetricInfo({ metric }: { metric: MetricInfoContent | undefined }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (!metric) return null;

  return (
    <span
      className="ed-metric-info"
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className="ed-metric-info__button"
        aria-label={`About ${metric.displayName}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(event) => {
          // The button can sit inside a clickable card: opening the explanation must never also follow the card's link.
          event.preventDefault();
          event.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span aria-hidden="true">i</span>
      </button>
      <span id={panelId} role="note" className="ed-metric-info__panel" hidden={!open}>
        <strong className="ed-metric-info__title">{metric.displayName}</strong>
        <span className="ed-metric-info__row">{metric.definition}</span>
        <span className="ed-metric-info__row"><em>How it is calculated:</em> {metric.formula}</span>
        <span className="ed-metric-info__row"><em>Includes:</em> {metric.included}</span>
        <span className="ed-metric-info__row"><em>Leaves out:</em> {metric.excluded}</span>
        <span className="ed-metric-info__row ed-metric-info__source">Source: {metric.sourceModule}</span>
      </span>
    </span>
  );
}
